import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../providers/AuthProvider';
import { useCompanyProject } from '../../providers/CompanyProjectProvider';
import { Company } from '../../types/company';
import { Project } from '../../types/project';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const {
    currentCompany,
    currentProject,
    companies,
    projects,
    setCurrentCompany,
    setCurrentProject,
    isCompanySelectorDisabled,
    isProjectSelectorDisabled,
  } = useCompanyProject();
  
  const [showIntegrations, setShowIntegrations] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  
  const integrationsRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (integrationsRef.current && !integrationsRef.current.contains(event.target as Node)) {
        setShowIntegrations(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="container">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center space-x-4">
              <h1 className="text-xl font-bold text-gray-900">Service Bot</h1>
            </div>

            {/* Desktop Menu (hidden on screens < 1000px) */}
            <nav className="hidden lg:flex items-center space-x-6">
              {!isAuthenticated && (
                <a href="/" className="text-gray-600 hover:text-gray-900 transition-colors">
                  Home
                </a>
              )}

              {isAuthenticated ? (
                <>
                  <a href="/omnichannel" className="text-gray-600 hover:text-gray-900 transition-colors">
                    Messages
                  </a>
                  
                  {/* Integrations Dropdown */}
                  <div className="relative" ref={integrationsRef}>
                    <button
                      onClick={() => {
                        setShowIntegrations(!showIntegrations);
                        setShowUserMenu(false);
                      }}
                      className="text-gray-600 hover:text-gray-900 transition-colors flex items-center"
                    >
                      Integrations
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {showIntegrations && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10 border border-gray-200">
                        <a
                          href="/integrations/google"
                          className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          onClick={() => setShowIntegrations(false)}
                        >
                          Google
                        </a>
                      </div>
                    )}
                  </div>
                  
                  {/* User Menu Dropdown */}
                  <div className="relative" ref={userMenuRef}>
                    <button
                      onClick={() => {
                        setShowUserMenu(!showUserMenu);
                        setShowIntegrations(false);
                      }}
                      className="text-gray-600 hover:text-gray-900 transition-colors flex items-center"
                    >
                      {user?.display_name || user?.email || 'User'}
                      <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {showUserMenu && (
                      <div className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg py-1 z-10 border border-gray-200">
                        {/* Company Selector */}
                        {currentCompany && (
                          <div className="px-4 py-2 border-b border-gray-200">
                            <label className="block text-xs text-gray-500 mb-1">Company</label>
                            <select
                              value={currentCompany?.id || ''}
                              onChange={async (e) => {
                                const companyId = parseInt(e.target.value);
                                if (companyId && companyId !== currentCompany?.id) {
                                  await setCurrentCompany(companyId);
                                  navigate(`/companies/${companyId}`);
                                  setShowUserMenu(false);
                                }
                              }}
                              disabled={isCompanySelectorDisabled}
                              className="form-input text-sm py-1 w-full"
                            >
                              {companies.map((company: Company) => (
                                <option key={company.id} value={company.id}>
                                  {company.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        
                        {/* Project Selector */}
                        {currentProject && (
                          <div className="px-4 py-2 border-b border-gray-200">
                            <label className="block text-xs text-gray-500 mb-1">Project</label>
                            <select
                              value={currentProject?.id || ''}
                              onChange={async (e) => {
                                const projectId = parseInt(e.target.value);
                                if (projectId && projectId !== currentProject?.id) {
                                  await setCurrentProject(projectId);
                                  navigate(`/projects/${projectId}/settings`);
                                  setShowUserMenu(false);
                                }
                              }}
                              disabled={isProjectSelectorDisabled}
                              className="form-input text-sm py-1 w-full"
                            >
                              {projects.map((project: Project) => (
                                <option key={project.id} value={project.id}>
                                  {project.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                        
                        {/* Company Link */}
                        {currentCompany && (
                          <a
                            href={`/companies/${currentCompany.id}`}
                            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                            onClick={() => setShowUserMenu(false)}
                          >
                            Company
                          </a>
                        )}
                        
                        <button
                          onClick={() => {
                            handleLogout();
                            setShowUserMenu(false);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          Logout
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <a href="/login" className="btn btn-outline">
                    Login
                  </a>
                  <a href="/register" className="btn btn-primary">
                    Register
                  </a>
                </>
              )}
            </nav>

            {/* Mobile Menu Button (visible on screens < 1000px) */}
            <button
              onClick={() => setShowMobileMenu(!showMobileMenu)}
              className="lg:hidden text-gray-600 hover:text-gray-900 p-2"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {showMobileMenu ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>

          {/* Mobile Menu (visible on screens < 1000px) */}
          {showMobileMenu && (
            <div className="lg:hidden border-t border-gray-200 pt-4 mt-4">
              <nav className="flex flex-col space-y-4">
                {!isAuthenticated && (
                  <a href="/" className="text-gray-600 hover:text-gray-900 transition-colors">
                    Home
                  </a>
                )}

                {isAuthenticated ? (
                  <>
                    <a href="/omnichannel" className="text-gray-600 hover:text-gray-900 transition-colors">
                      Messages
                    </a>
                    
                    {/* Integrations */}
                    <div>
                      <button
                        onClick={() => setShowIntegrations(!showIntegrations)}
                        className="text-gray-600 hover:text-gray-900 transition-colors flex items-center w-full"
                      >
                        Integrations
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {showIntegrations && (
                        <div className="ml-4 mt-2">
                          <a
                            href="/integrations/google"
                            className="block text-sm text-gray-600 hover:text-gray-900"
                            onClick={() => {
                              setShowIntegrations(false);
                              setShowMobileMenu(false);
                            }}
                          >
                            Google
                          </a>
                        </div>
                      )}
                    </div>
                    
                    {/* User Menu */}
                    <div>
                      <button
                        onClick={() => setShowUserMenu(!showUserMenu)}
                        className="text-gray-600 hover:text-gray-900 transition-colors flex items-center w-full"
                      >
                        {user?.display_name || user?.email || 'User'}
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {showUserMenu && (
                        <div className="ml-4 mt-2 space-y-2">
                          {/* Company Selector */}
                          {currentCompany && (
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Company</label>
                              <select
                                value={currentCompany?.id || ''}
                                onChange={async (e) => {
                                  const companyId = parseInt(e.target.value);
                                  if (companyId && companyId !== currentCompany?.id) {
                                    await setCurrentCompany(companyId);
                                    navigate(`/companies/${companyId}`);
                                    setShowMobileMenu(false);
                                    setShowUserMenu(false);
                                  }
                                }}
                                disabled={isCompanySelectorDisabled}
                                className="form-input text-sm py-1 w-full"
                              >
                                {companies.map((company: Company) => (
                                  <option key={company.id} value={company.id}>
                                    {company.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                          
                          {/* Project Selector */}
                          {currentProject && (
                            <div>
                              <label className="block text-xs text-gray-500 mb-1">Project</label>
                              <select
                                value={currentProject?.id || ''}
                                onChange={async (e) => {
                                  const projectId = parseInt(e.target.value);
                                  if (projectId && projectId !== currentProject?.id) {
                                    await setCurrentProject(projectId);
                                    navigate(`/projects/${projectId}/settings`);
                                    setShowMobileMenu(false);
                                    setShowUserMenu(false);
                                  }
                                }}
                                disabled={isProjectSelectorDisabled}
                                className="form-input text-sm py-1 w-full"
                              >
                                {projects.map((project: Project) => (
                                  <option key={project.id} value={project.id}>
                                    {project.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                          
                          {/* Company Link */}
                          {currentCompany && (
                            <a
                              href={`/companies/${currentCompany.id}`}
                              className="block text-sm text-gray-600 hover:text-gray-900"
                              onClick={() => {
                                setShowMobileMenu(false);
                                setShowUserMenu(false);
                              }}
                            >
                              Company
                            </a>
                          )}
                          
                          <button
                            onClick={() => {
                              handleLogout();
                              setShowMobileMenu(false);
                              setShowUserMenu(false);
                            }}
                            className="block text-sm text-gray-600 hover:text-gray-900 w-full text-left"
                          >
                            Logout
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <a href="/login" className="btn btn-outline">
                      Login
                    </a>
                    <a href="/register" className="btn btn-primary">
                      Register
                    </a>
                  </>
                )}
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="container">
          <div className="py-8">
            <div className="text-center text-gray-600">
              <p>&copy; {new Date().getFullYear()} AlfaTek. All rights reserved.</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}; 